import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  CODE_REPO_PROVIDER_LABEL,
  CODE_REPO_PROVIDERS,
  CODE_REPO_TOKEN_HINT,
  CODE_REPO_URL_PLACEHOLDER,
  guessRepoProvider,
  type CodeRepoProvider,
  type CodeRepoView,
} from '@1qa/shared';
import { api, errorMessage } from '@/lib/api';
import { timeAgo } from '@/lib/format';
import { CheckCircle2, GitBranch, Lock, RefreshCw } from '@/components/icons';
import { Button, Card, CardBody, CardHeader, Field, Input, Spinner, useConfirm, useToast } from '@/components/ui';

type TokenMode = 'keep' | 'replace' | 'remove';

/** The form to connect (or change) the repository. */
function RepoForm({ projectId, current, onDone, onCancel }: { projectId: string; current: CodeRepoView | null; onDone: () => void; onCancel?: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [provider, setProvider] = useState<CodeRepoProvider>(current?.provider ?? 'GITHUB');
  const [providerPicked, setProviderPicked] = useState(Boolean(current));
  const [url, setUrl] = useState(current?.url ?? '');
  const [branch, setBranch] = useState(current?.branch ?? '');
  const [username, setUsername] = useState(current?.username ?? '');
  const [token, setToken] = useState('');
  const [tokenMode, setTokenMode] = useState<TokenMode>(current?.hasToken ? 'keep' : 'replace');
  const [error, setError] = useState<string | null>(null);

  const connect = useMutation({
    mutationFn: () =>
      api.connectCodeRepo(projectId, {
        provider,
        url: url.trim(),
        branch: branch.trim(),
        username: provider === 'OTHER' ? username.trim() : '',
        // undefined keeps the saved token, '' removes it.
        token: tokenMode === 'keep' ? undefined : tokenMode === 'remove' ? '' : token.trim(),
      }),
    onSuccess: (r) => {
      qc.setQueryData(['code-repo', projectId], r);
      toast.success('Code repository connected');
      onDone();
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const onUrl = (v: string) => {
    setUrl(v);
    setError(null);
    // Pasting a github.com / gitlab address picks the provider, until the user picks one themselves.
    if (!providerPicked && /^https:\/\/[^/]+\//i.test(v)) setProvider(guessRepoProvider(v));
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-2 text-xs font-medium tracking-label text-fg-secondary uppercase">Where is the code?</div>
        <div className="grid grid-cols-3 gap-2">
          {CODE_REPO_PROVIDERS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => {
                setProvider(p);
                setProviderPicked(true);
                setError(null);
              }}
              className={clsx(
                'rounded-field px-3 py-2 text-sm font-medium ring-1 ring-inset transition',
                provider === p ? 'bg-primary-subtle/40 text-fg ring-primary' : 'bg-surface text-fg-secondary ring-border hover:ring-primary/60',
              )}
            >
              {CODE_REPO_PROVIDER_LABEL[p]}
            </button>
          ))}
        </div>
      </div>
      <Field label="Repository address" hint="The https address you would use with git clone. Self-hosted GitLab works too.">
        {(id) => <Input id={id} type="url" value={url} placeholder={CODE_REPO_URL_PLACEHOLDER[provider]} onChange={(e) => onUrl(e.target.value)} spellCheck={false} autoComplete="off" />}
      </Field>
      <div className={clsx('grid gap-4', provider === 'OTHER' && 'sm:grid-cols-2')}>
        <Field label="Branch (optional)" hint="Leave empty for the repository's default branch.">
          {(id) => <Input id={id} value={branch} placeholder={current?.defaultBranch ?? 'main'} onChange={(e) => setBranch(e.target.value)} spellCheck={false} autoComplete="off" />}
        </Field>
        {provider === 'OTHER' && (
          <Field label="User name" hint="The user name that goes with the token.">
            {(id) => <Input id={id} value={username} onChange={(e) => setUsername(e.target.value)} spellCheck={false} autoComplete="off" />}
          </Field>
        )}
      </div>
      {tokenMode === 'keep' ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-field bg-surface p-3 text-sm ring-1 ring-border ring-inset">
          <Lock className="size-3.5 text-fg-muted" />
          <span className="text-fg">An access token is saved.</span>
          <button type="button" className="text-xs font-medium text-primary hover:underline" onClick={() => setTokenMode('replace')}>
            Replace it
          </button>
          <button type="button" className="text-xs font-medium text-danger-text hover:underline" onClick={() => setTokenMode('remove')}>
            Remove it (public repository)
          </button>
        </div>
      ) : (
        <Field label="Access token (private repositories)" hint={`${CODE_REPO_TOKEN_HINT[provider]} Stored encrypted and never shown again. Leave empty for a public repository.`}>
          {(id) => (
            <Input
              id={id}
              type="password"
              value={token}
              placeholder={tokenMode === 'remove' ? 'The saved token will be removed' : provider === 'GITHUB' ? 'github_pat_…' : provider === 'GITLAB' ? 'glpat-…' : ''}
              onChange={(e) => {
                setToken(e.target.value);
                if (tokenMode === 'remove') setTokenMode('replace');
              }}
              autoComplete="new-password"
            />
          )}
        </Field>
      )}
      {error && (
        <p className="rounded-field bg-danger-subtle p-3 text-sm text-danger-text ring-1 ring-danger-line ring-inset" role="alert">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button onClick={() => connect.mutate()} loading={connect.isPending} disabled={!url.trim()} icon={<GitBranch className="size-4" />}>
          {current ? 'Save and check' : 'Connect repository'}
        </Button>
      </div>
      <p className="text-xs text-fg-muted">1QA checks it can read the repository before saving. The code is downloaded only for an audit and deleted after it.</p>
    </div>
  );
}

/** What is connected, with check / change / disconnect for Project Admins. */
function RepoSummary({ projectId, repo, canManage, onEdit }: { projectId: string; repo: CodeRepoView; canManage: boolean; onEdit: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const check = useMutation({
    mutationFn: () => api.checkCodeRepo(projectId),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success('The repository can be read');
        qc.invalidateQueries({ queryKey: ['code-repo', projectId] });
      } else toast.error(r.message ?? 'The repository could not be read');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const disconnect = useMutation({
    mutationFn: () => api.disconnectCodeRepo(projectId),
    onSuccess: () => {
      qc.setQueryData(['code-repo', projectId], null);
      toast.success('Code repository disconnected');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3 rounded-field bg-surface p-3 ring-1 ring-border ring-inset">
        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success-text" />
        <div className="min-w-0 text-sm">
          <div className="font-medium text-fg">
            {CODE_REPO_PROVIDER_LABEL[repo.provider]} ·{' '}
            <a href={repo.url} target="_blank" rel="noreferrer" className="break-all text-primary hover:underline">
              {repo.url.replace(/^https:\/\//, '')}
            </a>
          </div>
          <div className="mt-0.5 text-xs text-fg-muted">
            Branch {repo.branch ?? `${repo.defaultBranch ?? 'default'} (default)`} · {repo.hasToken ? 'private, access token saved' : 'public, no token'}
            {repo.checkedAt && ` · checked ${timeAgo(repo.checkedAt)}`}
            {repo.updatedByName && ` · connected by ${repo.updatedByName}`}
          </div>
        </div>
      </div>
      {canManage && (
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => check.mutate()} loading={check.isPending} icon={<RefreshCw className="size-3.5" />}>
            Check connection
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => confirm({ title: 'Disconnect this repository?', message: 'Code-level audits stop until a repository is connected again. The saved token is deleted.', confirmLabel: 'Disconnect', tone: 'danger' }).then((ok) => ok && disconnect.mutate())}
          >
            Disconnect
          </Button>
          <Button variant="secondary" size="sm" onClick={onEdit}>
            Change
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * The code repository (GitHub / GitLab / other Git) the code-level security audit reads. Shown in the Run dialog
 * when that audit is picked, on the scan page and in project settings. Everyone sees what is connected; only
 * `canManage` (Project Admins) connects or changes it.
 */
export function CodeRepoPanel({ projectId, canManage }: { projectId: string; canManage: boolean }) {
  const repo = useQuery({ queryKey: ['code-repo', projectId], queryFn: () => api.codeRepo(projectId) });
  const [editing, setEditing] = useState(false);
  useEffect(() => setEditing(false), [projectId]);

  if (repo.isLoading) return <Spinner />;
  if (repo.error) return <p className="text-sm text-danger-text">{errorMessage(repo.error)}</p>;
  const current = repo.data ?? null;
  if (current && !editing) return <RepoSummary projectId={projectId} repo={current} canManage={canManage} onEdit={() => setEditing(true)} />;
  if (!canManage) {
    return (
      <p className="rounded-field bg-warning-subtle p-3 text-sm text-fg ring-1 ring-warning/40 ring-inset">
        No code repository is connected yet. Ask a Project Admin to connect the app's GitHub / GitLab repository.
      </p>
    );
  }
  return <RepoForm projectId={projectId} current={current} onDone={() => setEditing(false)} onCancel={current ? () => setEditing(false) : undefined} />;
}

/** CodeRepoPanel as a page card. */
export function CodeRepoCard({ projectId, canManage }: { projectId: string; canManage: boolean }) {
  return (
    <Card>
      <CardHeader
        title="Code repository"
        description="Connect the app's source code from GitHub or GitLab for the code-level security audit. The black-box pentest does not need it."
      />
      <CardBody>
        <CodeRepoPanel projectId={projectId} canManage={canManage} />
      </CardBody>
    </Card>
  );
}
